/**
 * Visual query execution helpers used by ReportBuilderPage.
 *
 * Pure functions where possible — the orchestrator `runVisualQuery` makes API calls
 * but does not touch React state directly. The caller passes in `apiClient` (defaults to `api`)
 * and gets back a single result snapshot to merge into state.
 */
import { api as defaultApi } from '@/lib/api';

const NON_DATA_VIZ_TYPES = new Set(['ai_text', 'rich_text', 'image', 'shape']);

export function isDataVisual(viz) {
  return viz && !NON_DATA_VIZ_TYPES.has(viz.vizType);
}

/**
 * Convert a raw value into a filter clause.
 *  - `"YYYY-MM-DD|YYYY-MM-DD"` style strings become `between` clauses
 *  - everything else becomes `equals`
 */
function valueToFilter(field, value) {
  if (value && typeof value === 'object' && value.tk === 'text' && value.op) {
    const s = value.value;
    if (s === undefined || s === null || s === '') return null;
    return { field, operator: value.op, value: s };
  }
  if (typeof value === 'string' && value.includes('|') && value.split('|').length === 2) {
    const [start, end] = value.split('|');
    if (start && end) return { field, operator: 'between', value: [start, end] };
  }
  return { field, operator: 'equals', value };
}

/**
 * Expand a structured calendar-date-slicer value into filter clauses.
 * Returns null when `val` isn't a structured date value.
 */
function expandDateSlicer(field, val) {
  if (!val || typeof val !== 'object' || !val.dk) return null;
  if (val.dk === 'range' && val.start && val.endExcl) {
    return [{ field, operator: 'between', value: [val.start, val.endExcl] }];
  }
  if (val.dk === 'periods' && Array.isArray(val.periods) && val.periods.length) {
    return [{ field, operator: 'period_in', value: val.periods.map(p => [p.start, p.endExcl]) }];
  }
  if (val.dk === 'relative' && val.preset) {
    return [{ field, operator: 'relative', preset: val.preset }];
  }
  return [];
}

/**
 * Expand a numeric-range slicer value ({nk:'num', min, max}) into filter clauses.
 * Returns null when `val` isn't a numeric-range value. Supports open-ended bounds.
 */
function expandNumberRange(field, val) {
  if (!val || typeof val !== 'object' || val.nk !== 'num') return null;
  const hasMin = val.min !== undefined && val.min !== null && val.min !== '';
  const hasMax = val.max !== undefined && val.max !== null && val.max !== '';
  if (hasMin && hasMax) return [{ field, operator: 'between', value: [Number(val.min), Number(val.max)] }];
  if (hasMin) return [{ field, operator: 'gte', value: Number(val.min) }];
  if (hasMax) return [{ field, operator: 'lte', value: Number(val.max) }];
  return [];
}

/**
 * Collect all filters that should be applied to the given visual's query.
 * Includes drill, per-visual slicers (prefixed with `<vizId>_`), global filters,
 * and the cross-filter (only when source is a different visual).
 */
/**
 * Filter clauses for a single slicer (field, value) selection. Used by the
 * cascading-slicer loader to narrow one slicer's values by the others' picks.
 */
export function filtersForSelection(field, value) {
  if (!value) return [];
  const de = expandDateSlicer(field, value);
  if (de) return de;
  const nr = expandNumberRange(field, value);
  if (nr) return nr;
  if (typeof value === 'object' && value.preset) {
    return [{ operator: 'relative', preset: value.preset, ...(value.field ? { field: value.field } : {}) }];
  }
  if (field.startsWith('__')) return [];
  const vf = valueToFilter(field, value);
  return vf ? [vf] : [];
}

export function buildVisualFilters({ vizId, slicerSelections, globalFilterValues, crossFilter, drillFilters, filterDims, globalFilterDims, slicerScopes }) {
  const filters = [...(drillFilters || [])];
  // A slicer with an author-defined scope (cross-filter scope) only filters the
  // chosen visuals; `null`/empty means it affects all visuals (default).
  const inScope = (field) => {
    const sc = slicerScopes && slicerScopes[field];
    return !Array.isArray(sc) || sc.length === 0 || sc.includes(vizId);
  };
  // Only apply a stored slicer/global value if its field is STILL an active
  // drop-zone filter. Removing a slicer doesn't always scrub its saved value
  // (historically it deleted the wrong key), so without this gate a removed
  // time slicer keeps silently filtering the query. When the caller doesn't
  // pass the active-dim list, fall back to the old "apply everything" behavior.
  const activeSlicer = Array.isArray(filterDims) ? new Set(filterDims) : null;
  const activeGlobal = Array.isArray(globalFilterDims) ? new Set(globalFilterDims) : null;

  for (const [k, v] of Object.entries(slicerSelections || {})) {
    if (!v) continue;
    if (!k.startsWith(vizId + '_')) continue;
    // Strip the EXACT `<vizId>_` prefix — not just the first `_`-segment, since
    // a visual id can itself contain underscores (e.g. `v_1788457203741`).
    const field = k.slice(vizId.length + 1);
    if (activeSlicer && !activeSlicer.has(field)) continue;
    if (!inScope(field)) continue;
    const de = expandDateSlicer(field, v);
    if (de) { filters.push(...de); continue; }
    const nr = expandNumberRange(field, v);
    if (nr) { filters.push(...nr); continue; }
    const vf = valueToFilter(field, v);
    if (vf) filters.push(vf);
  }

  for (const [field, v] of Object.entries(globalFilterValues || {})) {
    if (!v) continue;
    // Keep reserved control keys (e.g. `__asof__`) flowing; only gate real dims.
    if (activeGlobal && !field.startsWith('__') && !activeGlobal.has(field)) continue;
    if (!field.startsWith('__') && !inScope(field)) continue;
    // Structured calendar-date-slicer value (range / periods / relative).
    const de = expandDateSlicer(field, v);
    if (de) { filters.push(...de); continue; }
    const nr = expandNumberRange(field, v);
    if (nr) { filters.push(...nr); continue; }
    // Relative date-range preset (e.g. {preset:'this_year_to_date'}) — resolved
    // server-side to a gte/lt range so scheduled reports stay current.
    if (typeof v === 'object' && v.preset) {
      filters.push({ operator: 'relative', preset: v.preset, ...(v.field ? { field: v.field } : {}) });
      continue;
    }
    if (field.startsWith('__')) continue;  // reserved control keys
    const vf = valueToFilter(field, v);
    if (vf) filters.push(vf);
  }

  if (crossFilter && crossFilter.sourceVisualId !== vizId) {
    filters.push({ field: crossFilter.field, operator: 'equals', value: crossFilter.value });
  }

  return filters;
}

/**
 * Resolve the visual's row/column dims (including markColor, markDetail) into the
 * actual database fields, accounting for virtual `_sourceField` mappings and
 * `sort_by_field` companions.
 */
export function resolveVisualDimensions({ rd, cd, markColor, markDetail, allFields }) {
  const allDims = [...rd, ...(cd ? [cd] : [])];
  if (markDetail && !allDims.includes(markDetail)) allDims.push(markDetail);
  if (markColor && !allDims.includes(markColor)) allDims.push(markColor);

  const virtualToReal = {};
  const queryDimsRaw = allDims.map(dim => {
    const fm = allFields.find(f => f.name === dim);
    if (fm?._sourceField) {
      virtualToReal[fm._sourceField] = virtualToReal[fm._sourceField] || [];
      virtualToReal[fm._sourceField].push(dim);
      return fm._sourceField;
    }
    return dim;
  });

  const queryDimsDeduped = [...new Set(queryDimsRaw)];
  const sortByDims = [];
  for (const dim of queryDimsDeduped) {
    const fieldMeta = allFields.find(f => f.name === dim);
    if (fieldMeta?.sort_by_field
      && !queryDimsDeduped.includes(fieldMeta.sort_by_field)
      && !sortByDims.includes(fieldMeta.sort_by_field)) {
      sortByDims.push(fieldMeta.sort_by_field);
    }
  }
  const queryDims = [...queryDimsDeduped, ...sortByDims];
  return { queryDims, virtualToReal, sortByDims, queryDimsDeduped };
}

/**
 * Split visual measures into regular vs formula-based, and bring in the dependency
 * fields needed to compute formulas client-side.
 */
// Time-intelligence formulas (YTD/QTD/MTD/ITD/PY/PP/YOY) apply a windowed date
// filter that CANNOT be recomputed on already-aggregated rows — they must be
// sent to the backend by measure name so the query engine builds the window.
const TI_FORMULA_RE = /\b(YTD|QTD|MTD|ITD|PY|PP|YOY)\s*\(/i;

// `safeEvalMath` understands ONLY numbers and + - * / ( ) — it tokenizes with
// /(\d+\.?\d*|[+\-*/().])/g and silently DROPS everything else. So a formula is
// safe to evaluate client-side (post-aggregation, per row) only when it reduces
// to pure arithmetic over already-aggregated measure columns.
//
// Anything with a function call, a string literal or a comparison MUST be
// resolved server-side by name, because it has to be computed INSIDE the
// aggregation. For example SUM_IF(CLAIM_AMOUNT, AMOUNT_TYPE_CODE='LOSSPAYMENT')
// compiles to SUM(CASE WHEN AMOUNT_TYPE_CODE = 'LOSSPAYMENT' THEN CLAIM_AMOUNT
// END) in the query engine; evaluating it client-side made safeEvalMath throw
// away SUM_IF( and the condition, leaving just the already-summed CLAIM_AMOUNT
// value — so the measure silently reported the unconditional total.
const FUNCTION_CALL_RE = /[A-Za-z_][A-Za-z0-9_]*\s*\(/;
const STRING_LITERAL_RE = /['"]/;
const COMPARISON_RE = /[<>=!]/;
const ARITHMETIC_ONLY_RE = /^[\s\d.+\-*/()]*$/;

/**
 * True when `formula` can be computed client-side from aggregated columns.
 * Conservative by design: when in doubt the measure goes to the server, which
 * has the full formula compiler (conditional aggregation, as-of, percent-share,
 * filter context, governed groups, data quality, first/last, cohort, forecast).
 */
export function isClientEvaluableFormula(formula) {
  if (!formula) return false;
  if (FUNCTION_CALL_RE.test(formula)) return false;
  if (STRING_LITERAL_RE.test(formula)) return false;
  if (COMPARISON_RE.test(formula)) return false;
  // Replace {Field Name} and bare identifiers with a number; only arithmetic
  // operators, digits and parentheses may remain.
  const stripped = String(formula)
    .replace(/\{[^{}]*\}/g, '0')
    .replace(/[A-Za-z_][A-Za-z0-9_]*/g, '0');
  return ARITHMETIC_ONLY_RE.test(stripped);
}

export function resolveVisualMeasures({ vm, allFields }) {
  const isFormula = (m) => {
    const f = allFields.find(ff => ff.name === m);
    if (!(f?._isModelMeasure && f?.formula)) return false;
    // Time-intelligence formulas are resolved server-side by name.
    if (TI_FORMULA_RE.test(f.formula)) return false;
    // A formula that references ANOTHER model formula/calc measure (e.g. a sum
    // of SUM_IF conditional measures) cannot be evaluated on the client from raw
    // columns — its referenced measures aggregate server-side as CASE WHEN. Send
    // it to the backend (resolve by name) so the real conditional SQL is built.
    const refsFormulaMeasure = (allFields || []).some(dep =>
      dep && dep !== f && dep._isModelMeasure && dep.formula && dep.name && f.formula.includes(dep.name));
    if (refsFormulaMeasure) return false;
    return isClientEvaluableFormula(f.formula);
  };
  const regularMeasures = vm.filter(m => !isFormula(m));
  const formulaMeasures = vm.filter(m => isFormula(m));
  const depMeasures = new Set(regularMeasures);
  for (const fm of formulaMeasures) {
    const f = allFields.find(ff => ff.name === fm);
    if (!f?.formula) continue;
    for (const dep of allFields) {
      if (dep.semantic_type === 'measure' && !(dep._isModelMeasure && dep.formula) && f.formula.includes(dep.name)) {
        depMeasures.add(dep.name);
      }
    }
  }
  return { queryMeasures: [...depMeasures], formulaMeasures };
}

/** Compute formula-based measures on each row using the supplied safe-math evaluator. */
export function applyFormulaMeasures(res, formulaMeasures, allFields, safeEvalMath) {
  if (!formulaMeasures.length || !res?.data) return res;
  return {
    ...res,
    data: res.data.map(row => {
      const newRow = { ...row };
      for (const fm of formulaMeasures) {
        const f = allFields.find(ff => ff.name === fm);
        if (!f?.formula) continue;
        try {
          let expr = f.formula;
          for (const ff of allFields) {
            if (row[ff.name] === undefined) continue;
            expr = expr.replace(
              new RegExp(`\\b${ff.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g'),
              typeof row[ff.name] === 'number' ? row[ff.name] : 0,
            );
          }
          newRow[fm] = safeEvalMath(expr);
        } catch { newRow[fm] = 0; }
      }
      return newRow;
    }),
  };
}

/** Rename `sort_by_field` columns to `_sort_<dim>` so they're hidden from charts/tables. */
export function unsuffixSortByColumns(res, sortByDims, queryDimsDeduped, allFields) {
  if (!sortByDims.length || !res?.data) return res;
  const sortByMap = {};
  for (const dim of queryDimsDeduped) {
    const fm = allFields.find(f => f.name === dim);
    if (fm?.sort_by_field && sortByDims.includes(fm.sort_by_field)) {
      sortByMap[fm.sort_by_field] = '_sort_' + dim;
    }
  }
  return {
    ...res,
    data: res.data.map(row => {
      const newRow = {};
      for (const [k, v] of Object.entries(row)) {
        if (sortByMap[k]) newRow[sortByMap[k]] = v;
        else newRow[k] = v;
      }
      return newRow;
    }),
  };
}

/** Copy source field values into virtual field names so date-part extraction works downstream. */
export function copyVirtualFieldValues(res, virtualToReal) {
  if (!Object.keys(virtualToReal).length || !res?.data) return res;
  return {
    ...res,
    data: res.data.map(row => {
      const newRow = { ...row };
      for (const [realField, virtuals] of Object.entries(virtualToReal)) {
        for (const vf of virtuals) newRow[vf] = row[realField];
      }
      return newRow;
    }),
  };
}

/** Read Top-N config off vizConfig and turn it into query parameters. */
export function buildTopNParams(vizCfg) {
  const topNCfg = vizCfg?.topN || {};
  const queryLimit = topNCfg.enabled && topNCfg.n ? topNCfg.n : 1000;
  const querySortBy = topNCfg.enabled && topNCfg.by ? topNCfg.by : undefined;
  const querySortDir = topNCfg.enabled ? (topNCfg.direction === 'bottom' ? 'asc' : 'desc') : 'asc';
  return { queryLimit, querySortBy, querySortDir };
}

/**
 * High-level: run the full query pipeline for a single visual.
 * Returns `{ result, pivotResult, triangleResult, comparisonResult, queryInfo }`
 * (any field may be `null`). The caller is responsible for merging into state.
 */
export async function runVisualQuery({
  vis, allFields, modelId,
  slicerSelections, globalFilterValues, globalFilterDims, crossFilter, drillFilters,
  markColor, markDetail, visualConfigs,
  safeEvalMath,
  asOfDate,
  slicerScopes,
  apiClient = defaultApi,
}) {
  const rd = vis.rowDims || [];
  const cd = vis.colDim || '';
  const vm = vis.valueMeasures || [];
  if (!rd.length && !vm.length) {
    return { result: null, pivotResult: null, triangleResult: null, comparisonResult: null, queryInfo: null, error: null };
  }

  const filters = buildVisualFilters({ vizId: vis.id, slicerSelections, globalFilterValues, crossFilter, drillFilters, filterDims: vis.filterDims || [], globalFilterDims, slicerScopes });
  const { queryDims, virtualToReal, sortByDims, queryDimsDeduped } = resolveVisualDimensions({ rd, cd, markColor, markDetail, allFields });
  const { queryMeasures, formulaMeasures } = resolveVisualMeasures({ vm, allFields });

  const vizCfg = (visualConfigs || {})[vis.id] || {};
  const { queryLimit, querySortBy, querySortDir } = buildTopNParams(vizCfg);
  // Optional per-dimension entity pins to disambiguate a field shared by several
  // role-playing dimensions (e.g. FULL_LEGAL_NAME → DIM_AGENT vs DIM_ADJUSTER).
  const dimensionEntities = vis.dimEntities || undefined;

  let res;
  try {
    res = await apiClient.executeQuery({
      model_id: modelId,
      dimensions: queryDims,
      measures: queryMeasures.length > 0 ? queryMeasures : vm,
      filters, limit: queryLimit, sort_by: querySortBy, sort_dir: querySortDir,
      dimension_entities: dimensionEntities,
      // "As of" date for to-date (YTD/QTD/MTD/ITD) measures. Per-card override
      // wins over the report/session-level date; both default to today server-side.
      as_of_date: vis.asOfDate || asOfDate || undefined,
      // Prior-period "vs same point last year" comparison — computed by default;
      // suppressed when the visual's toggle is off (saves the extra query).
      prior_period: vizCfg.showPriorPeriod === false ? false : undefined,
    });
  } catch (err) {
    // Even when the query fails (e.g. a fan-trap / unjoinable grouping that the
    // backend rejects with 400), still return a queryInfo carrying the attempted
    // plan + the error text so Query Details can EXPLAIN why there are no
    // results instead of unhelpfully saying "run the query first".
    const errQueryInfo = {
      error: (err && err.message) || 'Query failed.',
      dimensions: queryDims,
      measures: queryMeasures.length > 0 ? queryMeasures : vm,
      filters,
    };
    return { result: null, pivotResult: null, triangleResult: null, comparisonResult: null, queryInfo: errQueryInfo, error: err };
  }

  res = applyFormulaMeasures(res, formulaMeasures, allFields, safeEvalMath);
  res = unsuffixSortByColumns(res, sortByDims, queryDimsDeduped, allFields);
  res = copyVirtualFieldValues(res, virtualToReal);

  let pivotResult = null;
  // Only the pivot-table visual consumes pivotResult; charts pivot the flat
  // result client-side. Skipping this avoids a second (slow) round trip per
  // multi-series chart.
  if (vis.vizType === 'pivot' && cd && rd.length > 0 && vm.length > 0) {
    pivotResult = await apiClient.pivotQuery({ model_id: modelId, row_dimensions: rd, column_dimension: cd, measures: vm, filters, limit: 1000 });
  }

  let triangleResult = null;
  if (vis.vizType === 'loss_triangle' && rd.length >= 1 && vm.length >= 1) {
    const triCfg = vizCfg.triangleConfig || {};
    const originField = triCfg.originField || rd[0];
    // Development axis: prefer an explicit config, then the COLUMNS drop zone,
    // then a 2nd ROWS dimension. (Users naturally put the development period in
    // COLUMNS — which was previously ignored, leaving origin === dev.)
    const devField = triCfg.devField || cd || (rd.length >= 2 ? rd[1] : rd[0]);
    const valueField = vm[0];
    try {
      triangleResult = await apiClient.triangleQuery({
        model_id: modelId, origin_field: originField, dev_field: devField, value_field: valueField,
        filters, methods: triCfg.methods || ['simple', 'weighted', '3year', '5year'],
        tail_factor: triCfg.tailFactor || 1.0, manual_factors: triCfg.manualFactors || null,
        projection_method: triCfg.projectionMethod || 'chain_ladder',
        expected_loss_ratios: triCfg.expectedLossRatios || null,
        earned_premiums: triCfg.earnedPremiums || null,
      });
      // Surface a backend-reported failure instead of silently showing the
      // "not configured" empty state.
      if (triangleResult && triangleResult.error && originField === devField) {
        triangleResult = { error: 'Add a development-period dimension to COLUMNS (different from the origin in ROWS) to build the triangle.' };
      }
    } catch (err) {
      triangleResult = { error: (err && err.message) || 'Triangle query failed. Check the origin, development and value fields.' };
    }
  }

  let comparisonResult = null;
  const compCfg = vizCfg.periodComparison;
  if (compCfg?.enabled && compCfg?.timeField && compCfg?.currentValue && compCfg?.priorValue) {
    try {
      const parsedCurr = isNaN(compCfg.currentValue) ? compCfg.currentValue : Number(compCfg.currentValue);
      const parsedPrior = isNaN(compCfg.priorValue) ? compCfg.priorValue : Number(compCfg.priorValue);
      comparisonResult = await apiClient.comparePeriods({
        model_id: modelId,
        dimensions: [...rd, ...(cd ? [cd] : []), compCfg.timeField],
        measures: vm,
        time_field: compCfg.timeField,
        current_filter: { field: compCfg.timeField, value: parsedCurr },
        prior_filter: { field: compCfg.timeField, value: parsedPrior },
        filters, limit: queryLimit,
      });
    } catch {
      // period comparison is optional enrichment; ignore failures
    }
  }

  let queryInfo = null;
  try {
    queryInfo = await apiClient.explainQuery({ model_id: modelId, dimensions: queryDims, measures: vm, filters, limit: 1000, dimension_entities: dimensionEntities });
  } catch {
    // explain is diagnostic only; ignore failures
  }
  // Merge the ACTUAL executed query's debug info (duration, engine, native SQL)
  // so Query Details can show runtime + the real SQL that ran against the source.
  // NOTE: the backend compiles SQL for every query (even MongoDB-backed models),
  // so only surface `sql` when the query genuinely ran on a SQL datasource
  // (dialect other than "mongodb") — otherwise it's misleading.
  const dq = res?._debug_query || {};
  const ranOnSql = dq.dialect && dq.dialect !== 'mongodb';
  if (queryInfo || Object.keys(dq).length) {
    queryInfo = {
      ...(queryInfo || {}),
      duration_ms: dq.duration_ms ?? queryInfo?.duration_ms,
      executed_via: dq.executed_via,
      source_type: dq.source_type || queryInfo?.source_type,
      source_name: dq.source_name || queryInfo?.source_name,
      dialect: ranOnSql ? dq.dialect : (queryInfo?.dialect),
      sql: ranOnSql ? dq.sql : (queryInfo?.sql),
      served_by_aggregate: dq.served_by_aggregate || false,
      aggregate_name: dq.aggregate_name,
      aggregate_verified: dq.aggregate_verified,
    };
  }

  return { result: res, pivotResult, triangleResult, comparisonResult, queryInfo, error: null };
}
